import {useCallback} from 'react';

import {accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';

const useAccessPayload = ({nodeInfoMap, edits, targetIds, removeAll, isRoleVisibilityEnabled, getTreeSnapshot}) => {
    const mapNode = useCallback((id) => {
        const info = nodeInfoMap.get(id);
        return {id, type: info?.type, nestedRoute: info?.nestedRoute};
    }, [nodeInfoMap]);

    const mapNodeRich = useCallback((id) => {
        const info = nodeInfoMap.get(id);
        return {
            id,
            type: info?.type,
            nestedRoute: info?.nestedRoute,
            isPublished: info?.isPublished ?? true,
            permittedRoles: info?.permittedRoles || null,
        };
    }, [nodeInfoMap]);

    const serializeEditsState = useCallback(() => {
        const relevantIds = new Set([...edits.addedIncludeAll, ...edits.addedIndividual, ...edits.removed]);
        for (const id of [...relevantIds]) {
            const route = nodeInfoMap.get(id)?.nestedRoute || '';
            route.split('/').filter(Boolean).forEach(ancestorId => relevantIds.add(ancestorId));
        }
        const serializedNodeMap = {};
        for (const id of relevantIds) {
            const info = nodeInfoMap.get(id);
            if (info) serializedNodeMap[id] = info;
        }
        return {
            _editsState: {
                addedIncludeAll: [...edits.addedIncludeAll],
                addedIndividual: [...edits.addedIndividual],
                removed: [...edits.removed],
            },
            _nodeInfoMap: serializedNodeMap,
        };
    }, [edits, nodeInfoMap]);

    const buildSavePayload = useCallback(() => ({
        targetIds,
        removeAll,
        includeAll: [...edits.addedIncludeAll].map(mapNode),
        individual: [...edits.addedIndividual].map(mapNode),
        downgrades: [],
        removals: [...edits.removed].map(mapNode),
        ...serializeEditsState(),
    }), [targetIds, edits, removeAll, mapNode, serializeEditsState]);

    const buildFilterPayload = useCallback(() => {
        const includeAll = [...edits.addedIncludeAll].map(mapNodeRich);
        const individual = [...edits.addedIndividual].map(mapNodeRich);
        const summary = {projects: 0, folders: 0, items: 0};
        for (const node of includeAll) {
            if (node.type === accessItemTypeEnum.PROJECT) summary.projects++;
            else if (node.type === accessItemTypeEnum.FOLDER) summary.folders++;
        }
        summary.items = individual.filter(n => n.type === accessItemTypeEnum.ITEM).length;

        return {
            includeAll, individual, summary, isRoleVisibilityEnabled,
            ...serializeEditsState(),
            _treeSnapshot: getTreeSnapshot ? getTreeSnapshot() : null,
        };
    }, [edits, mapNodeRich, isRoleVisibilityEnabled, serializeEditsState, getTreeSnapshot]);

    return {buildSavePayload, buildFilterPayload};
};

export default useAccessPayload;
